import React, { useEffect, useMemo, useRef, type FC, type PropsWithChildren } from 'react'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { ActionCard } from '@playbooks/core/src/interfaces/ActionCard'
import { ComponentsApiContainer, IComponentsApiRegistry, IComponentApiProvider } from '@playbooks/action-cards-utils'

import { ActionCardsProps, createActionCardsStore, Store } from '../store'
import { ActionCardsConfig, ActionCardsSettings } from '../types'
import transformDataMap from '../utils/transformDataMap'
import useMountEffect from '../hooks/useMountEffect'
import { ProjectFileUploadServiceProvider } from '../services/projectFileUploadService'
import { isColorDark } from '../utils/isColorDark'
import HighlightElementsService from '../services/highlightElementsService'

type AppContextType = {
  settings: ActionCardsSettings
  config: ActionCardsConfig
  prevActionCardsDataRef: React.RefObject<ActionCard | null>
  rootRef: React.RefObject<HTMLDivElement | null>
  isAccentColorDark: boolean
  highlightedIds: number[]
  highlightElementsServiceRef: React.RefObject<HighlightElementsService>
  setHighlightedIds: (ids: number[]) => void
  resetHighlights: (idsToReset: number[]) => void
  /** Active AI Teammate sessions that are currently listening for user input. */
  activeAiTeammateSessions: AiTeammateSession[]
  /** Registers a new AI Teammate session that has started listening for user input. */
  registerActiveAiTeammateSession: (session: Omit<AiTeammateSession, 'startedAt'>) => void
  /** Unregisters an AI Teammate session that has stopped listening for user input. */
  unregisterActiveAiTeammateSession: (widgetId: number) => void
  componentsApiRegistry: IComponentsApiRegistry
  componentApiProvider: IComponentApiProvider
}

type AiTeammateSession = {
  widgetId: number
  startedAt: number
  stop: () => Promise<void>
}

type ActionCardsStore = ReturnType<typeof createActionCardsStore> & AppContextType

const AppContext = React.createContext<ActionCardsStore | null>(null)

export const AppContextProvider: FC<PropsWithChildren<ActionCardsProps>> = ({ children, ...props }) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const prevActionCardsDataRef = useRef<ActionCard | null>(null)
  const [highlightedIds, setHighlightedIds] = React.useState<number[]>([])
  const [activeAiTeammateSessionsByWidgetId, setActiveAiTeammateSessionsByWidgetId] = React.useState<
    Record<number, AiTeammateSession>
  >({})
  const highlightElementsServiceRef = useRef<HighlightElementsService>(new HighlightElementsService())
  const valueDependency = JSON.stringify(props.value)
  const componentsApiContainer = useRef<ComponentsApiContainer | null>(null)
  if (componentsApiContainer.current === null) {
    componentsApiContainer.current = new ComponentsApiContainer()
  }

  const resetHighlights = (idsToReset: number[]) => {
    setHighlightedIds(prev => prev.filter(id => !idsToReset.includes(id)))
  }

  /**
   * Register that a new AI Teammate session has started.
   * Necessary to track active sessions in the sticky Teammate indicator.
   */
  const registerActiveAiTeammateSession = (session: Omit<AiTeammateSession, 'startedAt'>) => {
    setActiveAiTeammateSessionsByWidgetId(currentSessions => ({
      ...currentSessions,
      [session.widgetId]: {
        ...session,
        startedAt: currentSessions[session.widgetId]?.startedAt ?? Date.now(),
      },
    }))
  }

  /**
   * Unregister an AI Teammate session that has stopped.
   * Necessary to track active sessions in the sticky Teammate indicator.
   */
  const unregisterActiveAiTeammateSession = (widgetId: number) => {
    setActiveAiTeammateSessionsByWidgetId(currentSessions => {
      if (!currentSessions[widgetId]) return currentSessions

      const remainingSessions = { ...currentSessions }
      delete remainingSessions[widgetId]
      return remainingSessions
    })
  }

  const store = useMemo(() => {
    return createActionCardsStore(
      { ...props },
      // This ref should be passed here because we need to assign a prev action cards value on every change
      // this ref is used as an encapsulated in context value to compare which changes of action cards is done
      // eslint-disable-next-line react-hooks/refs
      { prevActionCardsDataRef },
    )
    // We need to recreate the store every only when the value (Action Card) changes,
    // 'props' dependency not needed as we only need the initial values of these props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueDependency])

  const processedSettings = {
    ...props.settings,
    additionalData: transformDataMap(props.settings?.additionalData || {}),
  }

  useEffect(() => {
    highlightElementsServiceRef.current.reset()
    Promise.resolve([]).then(setHighlightedIds)
  }, [valueDependency])

  useMountEffect(() => {
    props.onInit?.(store.getState().actionCardsModel)
  })

  const fileUploadServiceProvider = props.config.fileUploadServiceProvider || new ProjectFileUploadServiceProvider()
  const isAccentColorDark = isColorDark(props.settings?.accentColor || '#000000')

  return (
    <AppContext.Provider
      value={{
        ...store,
        settings: processedSettings,
        config: {
          ...props.config,
          fileUploadServiceProvider,
        },
        prevActionCardsDataRef,
        rootRef,
        isAccentColorDark,
        highlightedIds,
        setHighlightedIds,
        resetHighlights,
        highlightElementsServiceRef,
        activeAiTeammateSessions: Object.values(activeAiTeammateSessionsByWidgetId),
        registerActiveAiTeammateSession,
        unregisterActiveAiTeammateSession,
        get componentsApiRegistry(): IComponentsApiRegistry {
          return componentsApiContainer.current!
        },
        get componentApiProvider(): IComponentApiProvider {
          return componentsApiContainer.current!
        },
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = (): Store & AppContextType => {
  const context = React.useContext(AppContext)

  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider')
  }

  return {
    ...useStore(
      context,
      useShallow(state => state),
    ),
    settings: context.settings,
    config: context.config,
    prevActionCardsDataRef: context.prevActionCardsDataRef,
    rootRef: context.rootRef,
    isAccentColorDark: context.isAccentColorDark,
    highlightedIds: context.highlightedIds,
    setHighlightedIds: context.setHighlightedIds,
    resetHighlights: context.resetHighlights,
    highlightElementsServiceRef: context.highlightElementsServiceRef,
    activeAiTeammateSessions: context.activeAiTeammateSessions,
    registerActiveAiTeammateSession: context.registerActiveAiTeammateSession,
    unregisterActiveAiTeammateSession: context.unregisterActiveAiTeammateSession,
    componentsApiRegistry: context.componentsApiRegistry,
    componentApiProvider: context.componentApiProvider,
  }
}
